import type { BackendTransaction, BackendTransactionItem, LineItem, ReceiptData, TransactionUpdateBody } from "../types/receiptTypes";

export const money = (value: number) => Math.round((value + Number.EPSILON * Math.max(1, Math.abs(value))) * 100) / 100;
export const isAllocation = (row: BackendTransaction) => row.dataOrigin === "Bank" || row.dataOrigin === "Manual";

/** Source classification never comes from a person's typed Category. */
export function bankClassification(row: BackendTransaction): { category: string; subcategory: string } {
  if (row.dataOrigin === "Bank") {
    try {
      const bank = JSON.parse(row.rawTransactionData || "null")?.bank;
      if (bank?.category) return { category: bank.category, subcategory: bank.subcategory || "Unallocated" };
    } catch { /* Legacy raw statement lines are not JSON. */ }
    const generated = row.items.find(item => item.origin === "AutoGenerated");
    if (generated?.category) return { category: generated.category, subcategory: generated.subcategory || "Unallocated" };
  }
  return { category: "Unallocated", subcategory: "Unallocated" };
}

export function displayedItems(row: BackendTransaction): BackendTransactionItem[] {
  const real = row.items.filter(item => item.origin !== "AutoGenerated");
  if (real.length) return real;
  return row.items.map((item, index) => row.dataOrigin === "Bank" && index === 0 && item.origin === "AutoGenerated"
    ? { ...item, quantity: 1, pricePerUnit: Math.abs(row.amount), totalPrice: Math.abs(row.amount) }
    : item);
}

export function unchangedBankSeed(row: BackendTransaction, items: LineItem[]): boolean {
  if (row.dataOrigin !== "Bank" || row.items.length !== 1 || items.length !== 1 || row.items[0].origin !== "AutoGenerated") return false;
  const source = row.items[0];
  const item = items[0];
  return item.id === source.id && item.name === source.name && item.fullName === source.fullName
    && (item.category ?? null) === source.category && (item.subcategory ?? null) === source.subcategory
    && item.quantity === 1 && money(item.unitPrice) === money(Math.abs(row.amount))
    && money(item.totalPrice) === money(Math.abs(row.amount));
}

export function transactionToReceipt(row: BackendTransaction): ReceiptData {
  return {
    id: row.id, merchantName: row.description, timestamp: row.timestamp,
    currency: row.currency === "EURO" ? "EUR" : row.currency,
    totalAmount: Math.abs(row.amount), additionalNotes: row.additionalNotes ?? undefined,
    items: displayedItems(row).map(item => ({
      id: item.id, origin: item.origin, name: item.name, fullName: item.fullName, quantity: item.quantity,
      unitPrice: item.pricePerUnit, totalPrice: item.totalPrice,
      category: item.category ?? undefined, subcategory: item.subcategory ?? undefined,
    })),
  };
}

/** All pages commit the same complete detail collection; server owns provenance and money facts. */
export function transactionSaveBody(row: BackendTransaction, detail: ReceiptData): TransactionUpdateBody {
  return {
    id: row.id, type: row.type, paymentMethodId: row.paymentMethodId,
    toPaymentMethodId: row.toPaymentMethodId,
    timestamp: row.dataOrigin === "Receipt" ? detail.timestamp ?? null : row.timestamp,
    amount: row.dataOrigin === "Receipt" ? detail.totalAmount : Math.abs(row.amount),
    currency: row.dataOrigin === "Receipt" ? detail.currency || row.currency : row.currency,
    description: detail.merchantName || row.description,
    additionalNotes: detail.additionalNotes ?? null, balanceAfter: row.balanceAfter,
    dataOrigin: row.dataOrigin, expectedDetailVersion: row.detailVersion,
    items: detail.items.map(item => ({
      id: item.id, name: item.name, fullName: item.fullName || item.name,
      category: item.category ?? null, subcategory: item.subcategory ?? null,
      quantity: item.quantity, pricePerUnit: item.unitPrice, totalPrice: money(item.unitPrice * item.quantity),
    })),
  };
}
